﻿import { QueryClient, useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';

import {
  GET_STOCK_CLIPS,
  STOCK_CLIP_API_URL,
  STOCK_CLIP_BY_ASSET_ID_QUERY_KEY,
  STOCK_CLIP_BY_ID_QUERY_KEY
} from '@/constants/QueryConstants';
import { apiUrl, getQueryParams } from '@/helpers';
import { ErrorResponse, Options } from '@/types';
import { CreateStockClipPayload, EditStockClipPayload } from '@/types/StockClipTypes';

export const stockClipEndpoints = {
  createStockClip: (payload: CreateStockClipPayload) =>
    axios.post(`${apiUrl()}${STOCK_CLIP_API_URL}`, payload),
  getStockClips: (
    pageSize: number,
    cursor: number,
    searchQuery?: string,
    videoThemeId?: number,
    isNegated?: boolean,
    sortingField?: string,
    sortingAsc?: boolean
  ) =>
    axios.get(
      `${apiUrl()}${STOCK_CLIP_API_URL}${getQueryParams({
        'paging.PageSize': pageSize as number,
        'paging.Cursor': cursor as number,
        'search.Text': searchQuery as string,
        'filter.VideoThemes[0].Id': videoThemeId as number,
        'filter.IsNegated': isNegated as boolean,
        'sorting.Field': sortingField as string,
        'sorting.Asc': sortingAsc as boolean
      })}`
    ),
  getStockClipById: (id: number) => axios.get(`${apiUrl()}${STOCK_CLIP_API_URL}/${id}`),
  deleteStockClip: (id: number) => axios.delete(`${apiUrl()}${STOCK_CLIP_API_URL}/${id}`),
  getStockClipByAssetId: (assetId: number) =>
    axios.get(`${apiUrl()}${STOCK_CLIP_API_URL}/getbyasset/${assetId}`),
  updateStockClip: (payload: EditStockClipPayload) =>
    axios.put(`${apiUrl()}${STOCK_CLIP_API_URL}`, payload)
};

export const useCreateStockClipMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(stockClipEndpoints.createStockClip, {
    onSuccess: (response) => {
      options.onSuccess(response);
      queryClient.invalidateQueries([GET_STOCK_CLIPS]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const useGetStockClips = (
  pageSize: number,
  enabled?: boolean,
  searchQuery?: string,
  videoThemeId?: number,
  isNegated?: boolean,
  sortingField?: string,
  sortingAsc?: boolean
) => {
  return useInfiniteQuery(
    [GET_STOCK_CLIPS, { pageSize, searchQuery, videoThemeId, isNegated, sortingField, sortingAsc }],
    ({ pageParam }) =>
      stockClipEndpoints.getStockClips(
        pageSize,
        pageParam,
        searchQuery,
        videoThemeId,
        isNegated,
        sortingField,
        sortingAsc
      ),
    {
      enabled: enabled,
      keepPreviousData: true,
      getNextPageParam: (lastPage) => {
        if (lastPage && lastPage.data?.nextCursor !== undefined) {
          return lastPage.data?.nextCursor;
        }
        return undefined;
      }
    }
  );
};

export const useGetStockClipByIdQuery = (id: number, options?: Options) => {
  return useQuery(
    [STOCK_CLIP_BY_ID_QUERY_KEY, { id }],
    () => stockClipEndpoints.getStockClipById(id),
    {
      enabled: !!id,
      onError: (error) => {
        options?.onError(error as ErrorResponse);
      }
    }
  );
};

export const useDeleteStockClipMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(stockClipEndpoints.deleteStockClip, {
    onSuccess: () => {
      options.onSuccess();
      queryClient.invalidateQueries([GET_STOCK_CLIPS]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const useGetStockClipByAssetIdQuery = (assetId: number, options?: Options) => {
  return useQuery(
    [STOCK_CLIP_BY_ASSET_ID_QUERY_KEY, { assetId }],
    () => stockClipEndpoints.getStockClipByAssetId(assetId),
    {
      enabled: !!assetId,
      onError: (error) => {
        options?.onError(error as ErrorResponse);
      }
    }
  );
};

export const useUpdateStockClip = (queryClient: QueryClient, options: Options) => {
  return useMutation(stockClipEndpoints.updateStockClip, {
    onSuccess: () => {
      options.onSuccess();
      queryClient.invalidateQueries([STOCK_CLIP_BY_ID_QUERY_KEY]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};
